import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertTriangle, Clock, ChevronRight } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { CTASection } from '../components/home/CTASection';

const sections = [
  {
    id: 'por-que',
    title: '¿Por qué hacer la transición a alimentación natural?',
    content: `La alimentación natural ofrece ingredientes frescos y reconocibles que el organismo de tu perro o gato puede aprovechar mejor. A diferencia de los ultraprocesados, los alimentos naturales conservan sus nutrientes y no contienen aditivos innecesarios.

Los beneficios potenciales incluyen mejor digestión, heces más formadas, piel y pelo más sanos, mayor energía y mejor control de peso. Sin embargo, cada animal es diferente y los resultados varían.`,
  },
  {
    id: 'antes-empezar',
    title: 'Antes de empezar: consideraciones importantes',
    content: `Antes de cambiar la dieta de tu animal, ten en cuenta:

• **Consulta con tu veterinario** si tu perro o gato tiene alguna condición de salud.
• **Planifica con antelación**: ten los ingredientes y suplementos necesarios.
• **Sé paciente**: la transición puede llevar de 1 a 4 semanas según el animal.
• **Observa las señales**: heces, energía, apetito y comportamiento te indicarán cómo va.`,
  },
];

const steps = [
  {
    day: 'Días 1-3',
    title: 'Introducción mínima',
    description: 'Añade un 10-20% de comida natural a su ración habitual. Observa la aceptación y las heces.',
    tips: ['Empieza con proteínas suaves (pollo, pavo)', 'Sirve a temperatura ambiente', 'No mezcles crudo con pienso si es posible'],
  },
  {
    day: 'Días 4-7',
    title: 'Aumento gradual',
    description: 'Si todo va bien, aumenta a 30-40% de comida natural. Mantén la observación.',
    tips: ['Introduce variedad poco a poco', 'Añade verduras en pequeña cantidad', 'Vigila consistencia de heces'],
  },
  {
    day: 'Días 8-14',
    title: 'Mitad y mitad',
    description: 'Llega al 50% de comida natural. Este suele ser el punto más crítico.',
    tips: ['Algunos animales pueden tener heces blandas temporalmente', 'Es normal, pero vigila que no sea excesivo', 'Puedes ralentizar si es necesario'],
  },
  {
    day: 'Días 15-21',
    title: 'Predominio natural',
    description: 'Aumenta a 70-80% de comida natural. El sistema digestivo ya se está adaptando.',
    tips: ['Introduce vísceras si no lo has hecho', 'Asegura variedad de proteínas', 'Mantén el registro de observaciones'],
  },
  {
    day: 'Días 22+',
    title: 'Transición completa',
    description: 'Si todo va bien, puedes completar la transición a 100% natural.',
    tips: ['Asegura que la dieta esté equilibrada', 'Considera suplementos si son necesarios', 'Continúa observando a largo plazo'],
  },
];

const commonErrors = [
  {
    error: 'Ir demasiado rápido',
    solution: 'El sistema digestivo necesita tiempo para adaptarse. Respeta los plazos.',
  },
  {
    error: 'No equilibrar la dieta',
    solution: 'Una dieta natural debe incluir proteína, vísceras, huesos y vegetales en proporciones adecuadas.',
  },
  {
    error: 'Olvidar los suplementos',
    solution: 'Algunos nutrientes (omega-3, vitamina E) pueden necesitar suplementación.',
  },
  {
    error: 'No adaptar las cantidades',
    solution: 'La comida natural es más densa nutricionalmente. Las cantidades son diferentes al pienso.',
  },
  {
    error: 'Ignorar las señales de alarma',
    solution: 'Vómitos persistentes, diarrea severa o rechazo total requieren parar y consultar.',
  },
];

const alarmSigns = [
  'Vómitos repetidos (más de 2 veces al día)',
  'Diarrea severa o con sangre',
  'Rechazo total del alimento durante más de 2 días',
  'Letargia o cambio brusco de comportamiento',
  'Pérdida de peso acelerada',
];

export const GuiaTransicionPage = () => {
  return (
    <>
      <SEOHead
        title="Guía de transición a alimentación natural | REALimenta"
        description="Pasos, checklist y errores comunes para hacer una transición segura y sostenible en perros y gatos."
        canonical="/guia-transicion"
      />

      <article className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[
            { href: '/recursos', label: 'Recursos' },
            { label: 'Guía de transición' }
          ]} />

          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mb-12"
          >
            <p className="text-brand-primary font-medium mb-4">Guía completa</p>
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-text-primary tracking-tighter mb-6"
              data-testid="guia-h1"
            >
              Cómo hacer la transición a alimentación natural
            </h1>
            <p className="text-text-secondary text-lg md:text-xl">
              Una guía paso a paso para cambiar la dieta de tu perro o gato de forma segura, 
              con consejos prácticos y señales a tener en cuenta.
            </p>
            <div className="flex items-center gap-4 mt-6 text-text-muted text-sm">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Lectura: 10 min
              </span>
            </div>
          </motion.header>

          {/* Table of Contents */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="card-brand max-w-xl mb-12"
            aria-label="Índice"
          >
            <h2 className="font-manrope font-bold text-text-primary mb-4">Índice</h2>
            <ul className="space-y-2">
              <li>
                <a href="#por-que" className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors">
                  <ChevronRight className="w-4 h-4" />
                  ¿Por qué hacer la transición?
                </a>
              </li>
              <li>
                <a href="#pasos" className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors">
                  <ChevronRight className="w-4 h-4" />
                  Pasos de la transición
                </a>
              </li>
              <li>
                <a href="#errores" className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors">
                  <ChevronRight className="w-4 h-4" />
                  Errores comunes
                </a>
              </li>
              <li>
                <a href="#alarmas" className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors">
                  <ChevronRight className="w-4 h-4" />
                  Señales de alarma
                </a>
              </li>
            </ul>
          </motion.nav>

          {/* Content Sections */}
          <div className="max-w-4xl space-y-16">
            {sections.map((section) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-manrope text-2xl md:text-3xl font-bold text-text-primary mb-6">
                  {section.title}
                </h2>
                <div className="prose prose-lg text-text-secondary whitespace-pre-line">
                  {section.content}
                </div>
              </motion.section>
            ))}

            {/* Steps */}
            <motion.section
              id="pasos"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-text-primary mb-8">
                Pasos de la transición
              </h2>
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div 
                    key={index} 
                    className="card-brand"
                    data-testid={`transition-step-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="px-3 py-1 bg-brand-primary text-white text-sm font-bold rounded-full">
                        {step.day}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-manrope font-bold text-text-primary mb-2">
                          {step.title}
                        </h3>
                        <p className="text-text-secondary mb-4">{step.description}</p>
                        <ul className="space-y-2">
                          {step.tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-text-muted">
                              <CheckCircle className="w-4 h-4 text-brand-accent flex-shrink-0 mt-0.5" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Common Errors */}
            <motion.section
              id="errores"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-text-primary mb-8">
                Errores comunes a evitar
              </h2>
              <div className="space-y-4">
                {commonErrors.map((item, index) => (
                  <div key={index} className="card-brand">
                    <p className="font-semibold text-text-primary mb-2">
                      ❌ {item.error}
                    </p>
                    <p className="text-text-secondary text-sm">
                      ✅ {item.solution}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Alarm Signs */}
            <motion.section
              id="alarmas"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-text-primary mb-6">
                Señales de alarma: cuándo parar y consultar
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800 mb-4">
                      Detén la transición y consulta al veterinario si observas:
                    </p>
                    <ul className="space-y-2">
                      {alarmSigns.map((sign, index) => (
                        <li key={index} className="text-red-700 text-sm">
                          • {sign}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="bg-brand-sage/20 rounded-3xl p-8 text-center"
            >
              <h3 className="font-manrope text-xl font-bold text-text-primary mb-4">
                ¿Necesitas ayuda personalizada?
              </h3>
              <p className="text-text-secondary mb-6">
                Cada animal es diferente. Si quieres un plan adaptado específicamente a tu caso, 
                puedo ayudarte con una consulta personalizada.
              </p>
              <Link
                to="/contacto"
                className="btn-primary inline-flex items-center gap-2 group"
                data-testid="guia-cta"
              >
                Solicitar consulta
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </article>
    </>
  );
};

export default GuiaTransicionPage;
